/**
 * Streaming Audio Player for Gemini Live
 * 
 * Handles real-time playback of base64-encoded PCM audio chunks
 * received from WebSocket streaming.
 */

export interface AudioChunk {
    data: string; // Base64 encoded PCM audio
    speaker_id: string;
    speaker_name: string;
    round?: string;
    sampleRate?: number;
}

export class StreamingAudioPlayer {
    private audioContext: AudioContext | null = null;
    private audioQueue: AudioChunk[] = [];
    private isPlaying: boolean = false;
    private currentSpeaker: string | null = null;
    private onSpeakerChange?: (speaker: string | null) => void;
    private onPlaybackComplete?: () => void;
    private nextStartTime: number = 0; // Track scheduled playback time for smooth streaming
    private readonly BUFFER_THRESHOLD = 0.1; // 100ms buffer to prevent gaps

    constructor(
        onSpeakerChange?: (speaker: string | null) => void,
        onPlaybackComplete?: () => void
    ) {
        this.onSpeakerChange = onSpeakerChange;
        this.onPlaybackComplete = onPlaybackComplete;
    }

    /**
     * Initialize Web Audio API context
     */
    private initializeAudioContext(): void {
        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        }

        // Always try to resume context if suspended
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume().then(() => {
                console.log('🔊 AudioContext resumed successfully');
            }).catch(err => {
                console.warn('⚠️ Failed to resume AudioContext:', err);
            });
        }
    }

    /**
     * Prepare audio context (call this on user interaction)
     */
    public async prepare(): Promise<void> {
        this.initializeAudioContext();
        if (this.audioContext && this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
            console.log('🔊 AudioContext prepared and resumed by user interaction');
        }
    }

    /**
     * Convert base64 to ArrayBuffer
     */
    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
     * Enqueue audio chunk for playback
     */
    enqueueChunk(chunk: AudioChunk): void {
        console.log(`🎵 Enqueued audio chunk from ${chunk.speaker_name} (queue size: ${this.audioQueue.length + 1})`);
        this.audioQueue.push(chunk);

        // Start playback if not already playing
        if (!this.isPlaying) {
            this.playNext();
        }
    }

    /**
     * Play next chunk in queue with smooth scheduling
     */
    private async playNext(): Promise<void> {
        if (this.audioQueue.length === 0) {
            this.isPlaying = false;
            // Don't clear speaker immediately - let the last chunk finish playing
            setTimeout(() => {
                if (this.audioQueue.length === 0) {
                    this.setSpeaker(null);
                    if (this.onPlaybackComplete) {
                        this.onPlaybackComplete();
                    }
                }
            }, 500);
            console.log('✅ Audio playback queue empty');
            return;
        }

        this.isPlaying = true;
        const chunk = this.audioQueue.shift()!;

        // Update current speaker
        this.setSpeaker(chunk.speaker_name);

        try {
            await this.playChunk(chunk);
        } catch (error) {
            console.error(`❌ Failed to play audio chunk from ${chunk.speaker_name}:`, error);
        }

        // Continue to next chunk immediately for smooth streaming
        this.playNext();
    }

    /**
     * Play individual audio chunk with precise scheduling for smooth streaming
     */
    private async playChunk(chunk: AudioChunk): Promise<void> {
        this.initializeAudioContext();

        if (!this.audioContext) {
            throw new Error('AudioContext not initialized');
        }

        // Ensure context is running
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }

        try {
            // Decode base64 to ArrayBuffer (Raw PCM 16-bit LE)
            const arrayBuffer = this.base64ToArrayBuffer(chunk.data);
            
            // Validate that we have data
            if (arrayBuffer.byteLength === 0) {
                console.warn(`⚠️ Empty audio chunk from ${chunk.speaker_name}`);
                return;
            }

            const int16Data = new Int16Array(arrayBuffer);
            
            // Validate that we have samples
            if (int16Data.length === 0) {
                console.warn(`⚠️ No audio samples from ${chunk.speaker_name}`);
                return;
            }

            const float32Data = new Float32Array(int16Data.length);

            // Convert Int16 to Float32 (-1.0 to 1.0) with proper normalization
            for (let i = 0; i < int16Data.length; i++) {
                float32Data[i] = int16Data[i] / 32768.0;
            }

            // Create AudioBuffer
            const channels = 1;
            const sampleRate = chunk.sampleRate || 24000;
            const audioBuffer = this.audioContext.createBuffer(channels, float32Data.length, sampleRate);

            // Copy data to channel
            audioBuffer.copyToChannel(float32Data, 0);

            // Create audio source
            const source = this.audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(this.audioContext.destination);

            // Calculate when to start this chunk for seamless playback
            const currentTime = this.audioContext.currentTime;
            
            // If nextStartTime is in the past or not set, start immediately
            if (this.nextStartTime < currentTime + this.BUFFER_THRESHOLD) {
                this.nextStartTime = currentTime + this.BUFFER_THRESHOLD;
            }

            // Schedule this chunk to start at the exact time the previous one ends
            source.start(this.nextStartTime);

            // Update nextStartTime for the next chunk
            const chunkDuration = audioBuffer.duration;
            this.nextStartTime += chunkDuration;

            // Don't wait for playback to complete - return immediately for continuous streaming
            
        } catch (error) {
            console.error(`❌ Audio processing error for ${chunk.speaker_name}:`, error);
            // Reset timing on error to recover
            this.nextStartTime = this.audioContext.currentTime;
            throw error;
        }
    }

    /**
     * Set current speaker and notify
     */
    private setSpeaker(speaker: string | null): void {
        if (this.currentSpeaker !== speaker) {
            this.currentSpeaker = speaker;
            if (this.onSpeakerChange) {
                this.onSpeakerChange(speaker);
            }
        }
    }

    /**
     * Get current playback status
     */
    getStatus(): { isPlaying: boolean; queueSize: number; currentSpeaker: string | null } {
        return {
            isPlaying: this.isPlaying,
            queueSize: this.audioQueue.length,
            currentSpeaker: this.currentSpeaker
        };
    }

    /**
     * Returns seconds of audio still scheduled on the AudioContext.
     * 0 means nothing is scheduled (or context is unavailable).
     */
    getRemainingPlaybackSeconds(): number {
        if (!this.audioContext) return 0;
        const remaining = this.nextStartTime - this.audioContext.currentTime;
        return remaining > 0 ? remaining : 0;
    }

    /**
     * Let all scheduled audio finish playing, then clean up.
     * Returns a promise that resolves once cleanup is done.
     */
    async drainAndCleanup(): Promise<void> {
        // Wait for the queue to be processed (all chunks scheduled)
        // The queue drains almost instantly since playNext() schedules
        // without waiting, so the real wait is for AudioContext playback.
        const remainingSeconds = this.getRemainingPlaybackSeconds();
        if (remainingSeconds > 0) {
            const waitMs = Math.ceil(remainingSeconds * 1000) + 500; // +500ms safety margin
            console.log(`🔊 Draining audio: ${remainingSeconds.toFixed(1)}s remaining, waiting ${waitMs}ms before cleanup`);
            await new Promise((resolve) => setTimeout(resolve, waitMs));
        }
        this.cleanup();
    }

    /**
     * Clear audio queue and stop current playback
     */
    clearQueue(): void {
        this.audioQueue = [];
        this.isPlaying = false;
        this.nextStartTime = 0; // Reset timing
        this.setSpeaker(null);
        console.log('🧹 Audio queue cleared and playback stopped');
    }

    /**
     * Cleanup resources
     */
    cleanup(): void {
        this.clearQueue();
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        console.log('🧹 Audio player cleaned up');
    }
}

export default StreamingAudioPlayer;
